<template>
  <div class="instrument-status-container">
    <div class="status-card" v-if="showTable">
      <div class="card-header">
        <div class="header-left">
          <span class="title-text">儀器狀態統計表</span>
        </div>
      </div>
      
      <el-table 
        :data="allStatisticsData" 
        border 
        stripe 
        style="width: 100%"
        :header-cell-style="headerStyle"
        empty-text="統計數據中..."
        class="custom-table"
      >
        <el-table-column prop="deviceType" label="儀器類型" align="center" min-width="150"></el-table-column>
        <el-table-column prop="total" label="總數" align="center" width="80">
          <template slot-scope="scope">
            <span class="total-count">{{ scope.row.total }}</span>
          </template>
        </el-table-column>
        
        <el-table-column prop="greenCount" label="正常" align="center" width="80">
          <template slot-scope="scope">
            <span class="green-text">{{ scope.row.greenCount }}</span>
          </template>
        </el-table-column>

        <el-table-column label="警戒值" align="center" width="100">
          <template slot-scope="scope">
            <el-tooltip v-if="scope.row.alList.length > 0" placement="top" effect="dark">
              <div slot="content" class="tooltip-content">
                <div v-for="item in scope.row.alList" :key="item.baseName" class="name-item">
                  {{ item.baseName }} <span v-if="item.remark" class="tooltip-remark">({{ item.remark }})</span>
                </div>
              </div>
              <div class="status-badge al-badge">{{ scope.row.alCount }}</div>
            </el-tooltip>
            <span v-else class="zero-text">0</span>
          </template>
        </el-table-column>

        <el-table-column label="行動值" align="center" width="110">
          <template slot-scope="scope">
            <el-tooltip v-if="scope.row.wslList.length > 0" placement="top" effect="dark">
              <div slot="content" class="tooltip-content">
                <div v-for="item in scope.row.wslList" :key="item.baseName" class="name-item">
                  {{ item.baseName }} <span v-if="item.remark" class="tooltip-remark">({{ item.remark }})</span>
                </div>
              </div>
              <div class="status-badge wsl-badge">{{ scope.row.wslCount }}</div>
            </el-tooltip>
            <span v-else class="zero-text">0</span>
          </template>
        </el-table-column>

        <el-table-column label="未更新" align="center" width="110">
          <template slot-scope="scope">
            <el-tooltip v-if="scope.row.downList.length > 0" placement="top" effect="dark">
              <div slot="content" class="tooltip-content">
                <div v-for="item in scope.row.downList" :key="item.baseName" class="name-item">
                  {{ item.baseName }} <span v-if="item.remark" class="tooltip-remark">({{ item.remark }})</span>
                </div>
              </div>
              <div class="status-badge down-badge">{{ scope.row.downCount }}</div>
            </el-tooltip>
            <span v-else class="zero-text">0</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="tech-dashboard-section" v-if="showChart && allStatisticsData.length > 0">
      <div class="dashboard-header-container">
        <div class="dashboard-header">
          <div class="glow-dot"></div>
          <span>儀器狀態監控</span>
        </div>
        
        <div class="chart-filter">
          <el-checkbox-group v-model="selectedChartTypes" size="small">
            <el-checkbox 
              v-for="item in allStatisticsData" 
              :key="item.deviceType" 
              :label="item.deviceType"
            >
              {{ item.deviceType.split(' ')[0] }} 
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>

      <div class="charts-wrapper" v-if="displayChartData.length > 0">
        <div 
          v-for="(item, index) in displayChartData" 
          :key="item.deviceType" 
          class="tech-card"
          :class="isPrimaryItem(index) ? 'primary-card' : 'secondary-card'"
        >
          <div class="card-corner top-left"></div>
          <div class="card-corner top-right"></div>
          <div class="card-corner bottom-left"></div>
          <div class="card-corner bottom-right"></div>
          
          <div class="chart-title-tech" :class="{'small-title': !isPrimaryItem(index)}">
            <i class="el-icon-data-line"></i> {{ item.deviceType }}
          </div>
          <div 
            ref="pieCharts" 
            class="chart-container" 
            :class="isPrimaryItem(index) ? 'primary-container' : 'secondary-container'"
          ></div>
        </div>
      </div>
      <div v-else class="empty-state">
        <el-empty description="請勾選上方圖表以顯示監控數據" :image-size="100"></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstrumentStatus',
  props: {
    instruments: {
      type: Array,
      default: () => [],
    },
    showTable: {
      type: Boolean,
      default: true,
    },
    showChart: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chartInstances: [],
      statisticsData: [],
      selectedChartTypes: [
        'STI 邊坡滑動監測桿',
        'TM 地滑計',
        'MC 土壤含水量計',
      ],
      headerStyle: {
        backgroundColor: '#ecf5ff',
        color: '#409eff',
        fontWeight: 'bold',
        fontSize: '14px',
      },
      deviceTypeNameMap: {
        'TM': 'TM 地滑計',
        'RAIN': 'RG 雨量計',
        'STI': 'STI 邊坡滑動監測桿',
        'ELP': 'ELP 水壓計',
        'MC': 'MC 土壤含水量計',
        'SAA': 'SAA 陣列式傾斜儀',
        'LC': 'LC 荷重計',
        'DS': 'DS 水位計',
        'ETI': 'ETI 傾斜計',
      },
    };
  },
  computed: {
    allStatisticsData() {
      return this.statisticsData;
    },
    displayChartData() {
      return this.allStatisticsData.filter(item => 
        this.selectedChartTypes.includes(item.deviceType),
      );
    },
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    timezone() {
      return this.$store.getters['env/timezone'];
    },
  },
  watch: {
    instruments: {
      handler: 'calculateStatistics',
      immediate: true,
      deep: true,
    },
    displayChartData: {
      handler() {
        this.$nextTick(() => {
          this.drawCharts();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.drawCharts();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.chartInstances.forEach(chart => {
      if (chart) chart.dispose();
    });
  },
  methods: {
    async getOutputById(outputId) {
      try {
        const res = await this.$axios({
          method: 'get',
          apiName: `output/${outputId}`,
        });
        return res.data; 
      } catch (err) {
        console.error('Error fetching output by ID:', err);
        return null;
      }
    },
    async getOutputByTime(instrumentId, Timestamp) {
      return this.$axios({
        category: 'project',
        method: 'post',
        apiName: `${this.selectedProject._id}/output?type=all`,
        data: {
          date: [Timestamp, Timestamp],
          interval: 'all',
          instrument: [instrumentId],
          timezone: this.timezone,
        },
      })
        .then(res => {
          if (!res.data.length) {
            console.warn(`No output data found for instrument ${instrumentId} at ${Timestamp}`);
          }
          // console.log('Output Data by Time:', res.data);
          return res.data;
        })
        .catch(err => {
          console.error('Error fetching output by time:', err);
        });
    },
    async calculateStatistics() {
      // console.log('instruments:', this.instruments);

      const standardRegex = /^TK(?<km>\d+)\+(?<m>\d{1,3})(?<side>[EWabcd])?(?:\((?<device_remark_0>[^)]*)\))?-(?<device>[A-Z]{2,4})-(?<level>\d+)(?:\((?<device_remark_1>[^)]*)\))?(?:-(?<device_remark_2>[A-Z0-9]{1,2}))?$/;
      const specialRegex = /^TK(?<km>\d+)\+(?<m>\d{1,3})(?<extra>[^-]*)-(?:.*-)?(?<device>[A-Z]{2,4})-(?<suffix>.*)$/;
      const simpleRegex = /^TK(?<km>\d+)-(?<device>[A-Z]{2,4})$/;

      const axisSuffixRegex = /-(EW|NS|X|Y|Z)$/i;
      const saaSuffixRegex = /-(?:X|Y)-[\d.]+M$/i;
      const lcSuffixRegex = /-LC-.*$/i;

      const statusWeight = { 'WSL': 4, 'AL': 3, 'down': 2, 'GREEN': 1 };
      const excludeKeywords = ['RSSI', 'Batt', 'Temp', 'UPS', '比較用'];

      const typeMap = {};

      for (const item of this.instruments) {
        const name = item.latestVersion?.name;
        const status = item.status || 'GREEN';
        const unit = item.latestVersion?.unit || '';
        const remarks = item.remark || [];
        const lastReadingId = item.last_reading || null;
        const instrumentId = item._id;

        if (!name || excludeKeywords.some(k => name.includes(k))) continue;

        let match = name.match(standardRegex) || name.match(specialRegex) || name.match(simpleRegex);
        let deviceType = (match && match.groups?.device) ? match.groups.device : '未知/其他';

        if (!deviceType || !this.deviceTypeNameMap[deviceType]) {
          continue;
        }

        let baseName = name;
        let latestRemark = null;

        if (status === 'down') {
          const latestRemarkObj = remarks.length > 0 ? remarks[remarks.length - 1] : null;

          if (latestRemarkObj) {
            const now = new Date();
            const startDate = new Date(latestRemarkObj.start_date);
            const endDate = new Date(latestRemarkObj.end_date);

            if (now >= startDate && now <= endDate) {
              latestRemark = latestRemarkObj.content;
            }
          }

          if (!latestRemark && lastReadingId) {
            try {
              const outputData = await this.getOutputById(lastReadingId);
              
              if (outputData && outputData.TIMESTAMP) {
                const lastTimestamp = new Date(outputData.TIMESTAMP);
                const now = new Date();
                const diffMs = now - lastTimestamp;
                const diffMinutes = Math.floor(diffMs / 60000);

                // 1. 計算多久沒回傳的文字描述
                let timeDiffText = '';
                if (diffMinutes < 60) {
                  timeDiffText = `已 ${diffMinutes} 分鐘無回傳`;
                } else {
                  timeDiffText = '超過 1 小時無回傳';
                }

                if (deviceType === 'STI') {
                  // 2. STI 特有邏輯：計算一小時前的速率
                  const oneHourAgo = new Date(lastTimestamp.getTime() - 60 * 60 * 1000);
                  const outputByTime = await this.getOutputByTime(instrumentId, oneHourAgo.toISOString());
                  
                  let rateText = '';
                  if (outputByTime && outputByTime.length > 0) {
                    const previousReading = outputByTime[0].reading2;
                    const currentReading = outputData.reading2;
                    if (previousReading !== undefined && currentReading !== undefined) {
                      const rate = ((currentReading - previousReading) / 1).toFixed(4);
                      rateText = `${rate} ${unit}/1Hr`;
                    }
                  }

                  // 3. 組合 STI 的顯示文字（同時顯示速率與未回傳時間）
                  if (rateText) {
                    latestRemark = `${timeDiffText}，最後讀值：${rateText}`;
                  } else {
                    latestRemark = timeDiffText; // 若抓不到一小時前的資料，至少顯示未回傳時間
                  }

                } else {
                  // 4. 其他裝置原本的邏輯
                  const lastReading = outputData.reading2;
                  latestRemark = `${timeDiffText}，最後讀值：${lastReading} ${unit}`;
                }
              }
            } catch (err) {
              console.error('Error fetching output data:', err);
            }
          } else {
            latestRemark = latestRemark || latestRemarkObj.content; // 無數據且無有效時間內備註時，退回顯示最後一個備註
          }
        }

        if (deviceType === 'SAA') baseName = name.replace(saaSuffixRegex, '');
        else if (deviceType === 'LC') baseName = name.replace(lcSuffixRegex, '');
        else baseName = name.replace(axisSuffixRegex, '');

        if (!typeMap[deviceType]) typeMap[deviceType] = {};
        
        const currentWeight = statusWeight[status] || 1;
        const existingWeight = typeMap[deviceType][baseName] ? statusWeight[typeMap[deviceType][baseName].status] : 0;

        if (currentWeight > existingWeight) {
          typeMap[deviceType][baseName] = {
            status,
            remark: latestRemark,
          };
        }
      }

      // 組裝並賦值給響應式 data
      this.statisticsData = Object.keys(typeMap).map(type => {
        const instruments = typeMap[type];
        const alList = [];
        const wslList = [];
        const downList = [];
        let safeCount = 0;

        Object.keys(instruments).forEach(baseName => {
          const itemData = instruments[baseName];
          const status = itemData.status;
          const remark = itemData.remark;

          if (status === 'WSL') {
            wslList.push({ baseName, remark });
          } else if (status === 'AL') {
            alList.push({ baseName, remark });
          } else if (status === 'down') {
            downList.push({ baseName, remark });
          } else {
            safeCount++;
          }
        });

        const sortByBaseName = (a, b) => a.baseName.localeCompare(b.baseName);
        const displayName = this.deviceTypeNameMap[type] || '未知儀器';

        return {
          deviceType: displayName,
          total: Object.keys(instruments).length,
          greenCount: safeCount,
          alCount: alList.length,
          alList: alList.sort(sortByBaseName),
          wslCount: wslList.length,
          wslList: wslList.sort(sortByBaseName),
          downCount: downList.length,
          downList: downList.sort(sortByBaseName),
        };
      }).sort((a, b) => b.total - a.total);
    },
    isPrimaryItem(index) {
      return index < 3;
    },
    drawCharts() {
      this.chartInstances.forEach(chart => {
        if (chart) chart.dispose();
      });
      this.chartInstances = [];

      const chartDOMs = this.$refs.pieCharts;
      if (!chartDOMs || chartDOMs.length === 0) return;

      this.displayChartData.forEach((item, index) => {
        const dom = chartDOMs[index];
        if (!dom) return;

        const chart = this.$echarts.init(dom);
        const isPrimary = this.isPrimaryItem(index);
        const total = item.greenCount + item.alCount + item.wslCount + item.downCount;
        
        const healthyRate = total > 0 ? ((item.greenCount / total) * 100).toFixed(1) : 0;

        const chartData = [
          { 
            value: item.greenCount, 
            name: '正常',
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#85ce61' },
                { offset: 1, color: '#67C23A' },
              ]),
            },
          },
          { 
            value: item.alCount, 
            name: '警戒值',
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#ebb563' },
                { offset: 1, color: '#E6A23C' },
              ]),
            },
          },
          { 
            value: item.wslCount, 
            name: '行動值',
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#f78989' },
                { offset: 1, color: '#F56C6C' },
              ]),
            },
          },
          { 
            value: item.downCount, 
            name: '未更新',
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#d3d4d6' },
                { offset: 1, color: '#909399' },
              ]),
            },
          },
        ];

        const option = {
          title: {
            text: `${healthyRate}%`,
            subtext: '正常率',
            left: 'center',
            top: isPrimary ? '38%' : '32%',
            textStyle: {
              color: '#409EFF',
              fontSize: isPrimary ? 24 : 18,
              fontWeight: 'bold',
              fontFamily: 'Arial, sans-serif',
            },
            subtextStyle: {
              color: '#909399',
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
          tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#ebeef5',
            textStyle: { color: '#303133' },
            formatter: '{b} : {c} 台 ({d}%)',
          },
          legend: {
            orient: 'horizontal',
            bottom: '2%',
            textStyle: {
              color: '#606266',
              fontSize: isPrimary ? 12 : 11,
            },
            itemWidth: 12,
            itemHeight: 12,
            icon: 'circle',
            formatter (name) {
              const target = chartData.find(d => d.name === name);
              const value = target ? target.value : 0;
              let percent = 0;
              if (total > 0) percent = parseFloat(((value / total) * 100).toFixed(2));
              return `${name} (${percent}%)`;
            },
          },
          series: [
            {
              type: 'pie',
              radius: isPrimary ? ['50%', '65%'] : ['40%', '55%'],
              center: ['50%', '45%'],
              silent: true,
              itemStyle: {
                color: '#F2F6FC', 
              },
              label: { show: false },
              data: [{ value: 1 }],
            },
            {
              name: '儀器狀態',
              type: 'pie',
              radius: isPrimary ? ['50%', '65%'] : ['40%', '55%'],
              center: ['50%', '45%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 4,
                borderColor: '#ffffff',
                borderWidth: 2,
              },
              label: { show: false },
              labelLine: { show: false },
              data: chartData,
            },
          ],
        };

        chart.setOption(option);
        this.chartInstances.push(chart);
      });
    },
    handleResize() {
      this.chartInstances.forEach(chart => {
        if (chart) chart.resize();
      });
    },
  },
};
</script>

<style scoped>
.tooltip-remark {
  color: #a6a9ad;
  font-size: 12px;
  margin-left: 5px;
}

.instrument-status-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.status-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  padding: 20px;
}

.tech-dashboard-section {
  flex: 1;
  padding: 20px 25px;
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  position: relative;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ebeef5;
}

.header-left {
  display: flex;
  align-items: center;
}

.title-text {
  margin-top: 10px;
  margin-left: 10px;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.tech-dashboard-section {
  padding: 20px 25px;
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  position: relative;
}

.dashboard-header-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
  border-bottom: 1px dashed #ebeef5;
  padding-bottom: 15px;
}

.dashboard-header {
  display: flex;
  align-items: center;
  color: #409EFF;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

.glow-dot {
  width: 10px;
  height: 10px;
  background-color: #409EFF;
  border-radius: 50%;
  margin-right: 10px;
  box-shadow: 0 0 8px rgba(64, 158, 255, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
}

.chart-filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-label {
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}

::v-deep .el-checkbox {
  margin-right: 15px;
}

.charts-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 20px; 
}

.tech-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: center;
  padding-top: 15px;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.tech-card:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 15px rgba(64, 158, 255, 0.15);
  transform: translateY(-3px);
}

.card-corner {
  position: absolute;
  width: 10px;
  height: 10px;
  border-color: #409EFF;
  border-style: solid;
  opacity: 0.8;
}
.top-left { top: -1px; left: -1px; border-width: 2px 0 0 2px; border-top-left-radius: 4px; }
.top-right { top: -1px; right: -1px; border-width: 2px 2px 0 0; border-top-right-radius: 4px; }
.bottom-left { bottom: -1px; left: -1px; border-width: 0 0 2px 2px; border-bottom-left-radius: 4px; }
.bottom-right { bottom: -1px; right: -1px; border-width: 0 2px 2px 0; border-bottom-right-radius: 4px; }

.primary-card {
  width: calc(33.33% - 20px);
  min-width: 260px;
}

.secondary-card {
  flex: 1 1 18%; 
  max-width: 25%;
  min-width: 180px;
}

.primary-container { width: 100%; height: 260px; }
.secondary-container { width: 100%; height: 210px; }

.chart-title-tech {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  letter-spacing: 0.5px;
  z-index: 10;
  position: relative;
}
.small-title {
  font-size: 14px;
  color: #606266;
}

.empty-state {
  padding: 40px 0;
}

@media screen and (max-width: 1200px) {
  .primary-card { width: calc(50% - 20px); }
  .secondary-card { flex: 1 1 30%; max-width: 33%; }
}
@media screen and (max-width: 768px) {
  .primary-card, .secondary-card { width: 100%; flex: none; max-width: 100%; }
}

.status-badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-weight: bold; font-size: 13px; cursor: pointer; }
.al-badge { background-color: #fdf6ec; color: #e6a23c; border: 1px solid #faecd8; }
.wsl-badge { background-color: #fef0f0; color: #f56c6c; border: 1px solid #fde2e2; }
.down-badge { background-color: #f4f4f5; color: #909399; border: 1px solid #e9e9eb; }
.total-count { color: #409eff; font-weight: bold; }
.green-text { color: #67c23a; font-weight: bold; }
.zero-text { color: #c0c4cc; }
.tooltip-content { max-height: 200px; overflow-y: auto; }
.name-item { padding: 2px 0; border-bottom: 1px solid #505050; }
</style>